import { useEffect, useRef, useState } from "react";
import { ApplePay, CreditCard, GooglePay, PaymentForm } from "react-square-web-payments-sdk";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

let cfgPromise = null;
export const loadPaymentConfig = () => (cfgPromise ||= api.get("/payments/config").then((r) => r.data).catch((e) => { cfgPromise = null; throw e; }));

const btnCss = { backgroundColor: "#0ea5e9", fontSize: "15px", color: "#fff", "&:hover": { backgroundColor: "#38bdf8" } };

const SquarePay = ({ cfg, amount, onToken, busy, label, buyerName }) => (
  <PaymentForm
    applicationId={cfg.square.application_id}
    locationId={cfg.square.location_id}
    cardTokenizeResponseReceived={(token, buyer) => {
      if (token.status !== "OK") return toast.error("Payment could not be verified");
      return onToken(token.token, buyer?.token || null);
    }}
    createPaymentRequest={() => ({ countryCode: "US", currencyCode: "USD", total: { amount: amount.toFixed(2), label: "Reflections Auto Detailing" } })}
    createVerificationDetails={() => ({ amount: amount.toFixed(2), currencyCode: "USD", intent: "CHARGE", billingContact: { givenName: buyerName || "" } })}
  >
    <div className="space-y-2 mb-3" data-testid="pay-wallets"><ApplePay /><GooglePay /></div>
    <CreditCard buttonProps={{ isLoading: busy, css: btnCss }}>{busy ? "Processing…" : label}</CreditCard>
  </PaymentForm>
);

let collectPromise = null;
const loadCollect = (key) => (collectPromise ||= new Promise((resolve, reject) => {
  if (window.CollectJS) return resolve(window.CollectJS);
  const s = document.createElement("script");
  s.src = "https://secure.nmi.com/token/Collect.js";
  s.setAttribute("data-tokenization-key", key);
  s.onload = () => resolve(window.CollectJS);
  s.onerror = () => { collectPromise = null; reject(new Error("load failed")); };
  document.head.appendChild(s);
}));

const NmiPay = ({ cfg, amount, onToken, busy, label }) => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const cb = useRef(onToken);
  cb.current = onToken;
  useEffect(() => {
    let alive = true;
    const fail = () => alive && setFailed(true);
    const timer = setTimeout(() => !document.querySelector("#nmi-ccnumber iframe") && fail(), 12000);
    loadCollect(cfg.nmi.tokenization_key).then((C) => {
      if (!alive) return;
      try { C.configure({
        variant: "inline",
        price: amount.toFixed(2), currency: "USD", country: "US",
        fields: {
          ccnumber: { selector: "#nmi-ccnumber", title: "Card number", placeholder: "Card number" },
          ccexp: { selector: "#nmi-ccexp", title: "Expiration", placeholder: "MM / YY" },
          cvv: { selector: "#nmi-cvv", title: "CVV", placeholder: "CVV", display: "required" },
          applePay: { selector: "#nmi-applepay", requiredBillingContactFields: ["postalAddress"], type: "buy" },
          googlePay: { selector: "#nmi-googlepay", buttonType: "buy", buttonColor: "white" },
        },
        customCss: { color: "#ffffff", "background-color": "transparent", "font-size": "15px" },
        fieldsAvailableCallback: () => setReady(true),
        callback: (r) => (r?.token ? cb.current(r.token, null) : toast.error("Card could not be read")),
      }); } catch { fail(); }
    }).catch(fail);
    return () => { alive = false; clearTimeout(timer); };
  }, [cfg.nmi.tokenization_key, amount]);
  const box = "h-11 rounded-xl border border-white/10 bg-white/[0.03] px-3 flex items-center";
  if (failed && !ready) return <p className="text-center text-amber-300 text-sm" data-testid="pay-nmi-failed">Online card payments are temporarily unavailable. Call (520) 380-4407 to pay.</p>;
  return (
    <div className="space-y-3" data-testid="pay-nmi-form">
      <div className="space-y-2" data-testid="pay-wallets"><div id="nmi-applepay" /><div id="nmi-googlepay" /></div>
      <div id="nmi-ccnumber" className={box} />
      <div className="grid grid-cols-2 gap-2"><div id="nmi-ccexp" className={box} /><div id="nmi-cvv" className={box} /></div>
      <button type="button" disabled={!ready || busy} onClick={() => window.CollectJS?.startPaymentRequest()} data-testid="pay-nmi-submit"
        className="w-full h-12 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
        {busy || !ready ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? "Processing…" : label}
      </button>
    </div>
  );
};

export const SaveCardBox = () => (
  <p className="flex items-center gap-2 text-[11px] text-gray-500" data-testid="pay-save-card">
    <Lock className="w-3 h-3 shrink-0" /> Your card is kept on file securely with our processor for faster future payments. We never store card numbers.
  </p>
);

export const FeeLine = ({ fee, pct }) => (fee > 0 ? (
  <div className="flex justify-between text-xs text-gray-400" data-testid="pay-card-fee"><span>Card processing fee ({pct}%)</span><span>${fee.toFixed(2)}</span></div>
) : null);

/** Renders the active processor's checkout (Square or Wholesale/NMI) incl. Apple Pay / Google Pay. onToken(sourceId, verificationToken). */
export const CardPay = ({ amount, onToken, busy, label, buyerName, fallback }) => {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { loadPaymentConfig().then(setCfg).catch(() => setErr(true)); }, []);
  if (err || (cfg && !cfg.configured)) return fallback || <p className="text-center text-amber-300 text-sm" data-testid="pay-not-configured">Online payments are being set up. Call (520) 380-4407 to pay.</p>;
  if (!cfg) return <Loader2 className="w-5 h-5 animate-spin text-sky-400 mx-auto" />;
  return (
    <div data-testid={`pay-processor-${cfg.active}`}>
      {cfg.active === "nmi"
        ? <NmiPay key={amount.toFixed(2)} cfg={cfg} amount={amount} onToken={onToken} busy={busy} label={label} />
        : <div data-testid="pay-square-form"><SquarePay cfg={cfg} amount={amount} onToken={onToken} busy={busy} label={label} buyerName={buyerName} /></div>}
      <p className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1 mt-3" data-testid="pay-secured-by"><Lock className="w-3 h-3" /> Payments secured by {cfg.label}. We never see your card number.</p>
    </div>
  );
};
